/* eslint-disable react/require-default-props */
/* eslint-disable no-nested-ternary */
import React from 'react';
import styled from '@emotion/styled';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import { Spacings, Fonts } from '@styles/index';
import textHelper from '@utils/textHelper';
import { NOTES } from '@constants/segment';
import TopicTooltip, { ITopicTooltipFn } from '../CommonCards/TopicTooltip';

interface ITopicCardProps {
  title: string;
  color: string;
  chaptersCount: number;
  onClick: () => void;
  countSubtext?: string;
  className?: string;
  educatorCoveredTopic: boolean;
  educatorName?: string;
  isEpubAvailable: boolean;
  isNotesAvailable?: boolean;
  source: string;
  topicUID: string;
}

interface CursorProps {
  $cursor: string;
  $disabled: boolean;
}

const MAX_TOPIC_LENGTH = 28;

const Wrapper = styled.div<CursorProps>`
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_6B} ${Spacings.SPACING_6B}
    ${Spacings.SPACING_5B};
  background: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${({ $disabled }) => ($disabled ? 'none' : boxShadowGenerator())};
  display: flex;
  align-items: center;
  width: 272px;
  height: 91px;
  cursor: ${({ $cursor }) => $cursor};
  grid-column: span 6;
  &:hover {
    box-shadow: ${({ $disabled }) =>
      $disabled
        ? 'none'
        : boxShadowGenerator({
            xOffset: '0px',
            yOffset: Spacings.SPACING_1B,
            blur: Spacings.SPACING_2B,
            spread: '0px'
          })};
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: ${Spacings.SPACING_4B};
  width: 100%;
`;

const BorderSpan = styled.div`
  height: ${({
    // @ts-ignore
    $height
  }) => $height};
  width: ${Spacings.SPACING_1B};
  background: ${({ color }) => color};
  opacity: 50%;
  border-radius: 30px;
`;

const StyH6 = styled(H6)`
  margin-bottom: ${Spacings.SPACING_1B};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({
    // @ts-ignore
    $truncateToOneLine
  }) => ($truncateToOneLine ? 1 : 2)};
  line-clamp: ${({
    // @ts-ignore
    $truncateToOneLine
  }) => ($truncateToOneLine ? 1 : 2)};
  -webkit-box-orient: vertical;
`;

const EducatorName = styled(P2)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;
const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SoonTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-divider);
  border-radius: ${Spacings.SPACING_1B};
  text-transform: uppercase;
  font-weight: ${Fonts.HEAVY};
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 10px;
  color: var(--color-text-secondary);
  height: 14px;
  min-width: 42px;
`;

const TopicCard: React.FC<ITopicCardProps> = ({
  title,
  chaptersCount,
  onClick,
  color,
  countSubtext,
  className,
  educatorCoveredTopic = true,
  educatorName,
  isEpubAvailable = true,
  isNotesAvailable,
  source,
  topicUID
}) => {
  const popupRef = React.useRef<ITopicTooltipFn>();
  const countText = textHelper.pluralize(
    chaptersCount,
    countSubtext || 'chapter'
  );

  const onChapterLeave = () => {
    popupRef.current.onChapterLeave();
  };
  const onChapterHover = (educatorCoveredTopic) => (event) => {
    popupRef.current.onChapterHover(event, educatorCoveredTopic);
  };

  // Used in Educator Library and Notes Page
  const isEpubDisabled = source === NOTES && !isEpubAvailable;
  const isNotesDisabled = source === NOTES && !isNotesAvailable;
  const disabledTopic =
    !educatorCoveredTopic || (isEpubDisabled && isNotesDisabled);

  return (
    <>
      <Wrapper
        onClick={onClick}
        className={className}
        onMouseEnter={onChapterHover(educatorCoveredTopic)}
        onMouseLeave={onChapterLeave}
        $disabled={disabledTopic}
        $cursor={disabledTopic ? 'default' : 'pointer'}
        data-testid={`topic-card-${topicUID}`}
      >
        <BorderSpan
          color={
            isEpubDisabled && isNotesDisabled
              ? 'var(--color-divider)'
              : !educatorCoveredTopic
              ? 'var(--color-text-secondary)'
              : color
          }
          data-testid={`border-span${topicUID}`}
          // @ts-ignore
          $height={
            title.length > MAX_TOPIC_LENGTH || educatorName ? '58px' : '37px'
          }
        />
        <InfoWrapper>
          <InnerWrapper>
            <StyH6
              color={
                disabledTopic
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-primary)'
              }
              data-testid={`topic-title-${topicUID}`}
              // @ts-ignore
              $truncateToOneLine={!!educatorName}
            >
              {title.charAt(0).toUpperCase() + title.slice(1)}
            </StyH6>
            {isEpubDisabled && isNotesDisabled ? (
              <SoonTag data-testid={`soon-tag-${topicUID}`}>SOON</SoonTag>
            ) : null}
          </InnerWrapper>
          {!isEpubDisabled || !isNotesDisabled ? (
            <P2
              color="var(--color-text-secondary)"
              data-testid={`count-text-${topicUID}`}
            >
              {countText}
            </P2>
          ) : null}
          {educatorName && <EducatorName>{educatorName}</EducatorName>}
        </InfoWrapper>
      </Wrapper>
      <TopicTooltip ref={popupRef} />
    </>
  );
};

export default TopicCard;
